import { normalizeIsbn } from "@libry-content/common";
import { ValueOf } from "@libry-content/types";

export interface ControlField {
  tag: string;
  value?: string;
}

export interface Subfield {
  code: string;
  value?: string;
}

export interface RawSubfield {
  _: string;
  $: { code: string };
}

export interface RawMarcRecord {
  leader?: string;
  controlfield: { _: string; $: { tag: string } }[];
  datafield: { subfield: RawSubfield[]; $: { tag: string; ind1?: string; ind2?: string } }[];
}

export type WorkType =
  | "Language based work"
  | "Musical work"
  | "Moving image work"
  | "Picture work"
  | "Cartographic work"
  | "Interactive work";

const workTypeLabels = {
  "Language based work": "Bok",
  "Musical work": "Musikk",
  "Moving image work": "Film",
  "Picture work": "Bildeverk",
  "Cartographic work": "Kart og atlas",
  "Interactive work": "Spill",
} as const satisfies Record<WorkType, string>;

type WorkTypeLabel = ValueOf<typeof workTypeLabels>;

export const getWorkTypeLabel = <T>(params: { workType?: WorkType; t: (key: WorkTypeLabel | "Ukjent format") => T }) =>
  params.t((params.workType && workTypeLabels[params.workType]) || "Ukjent format");

const match = (pattern: string | RegExp, value: string) =>
  typeof pattern === "string" ? value === pattern : !!value.match(pattern);

const trimBrackets = (value: string | undefined) => value?.replace(/[[\]]/g, "");

export class DataField {
  tag: string;
  ind1: string;
  ind2: string;
  subfields: Subfield[];

  constructor({ tag, ind1, ind2, subfields }: any) {
    this.tag = tag;
    this.ind1 = ind1;
    this.ind2 = ind2;
    this.subfields = subfields;
  }

  /**
   * Return all subfields with a specific code. Returns empty array if none found.
   */
  getSubfields(code: string | RegExp) {
    return this.subfields.filter((subfield) => match(code, subfield.code));
  }

  /**
   * Return all subfield values
   */
  getSubfieldValues(code: string | RegExp) {
    return this.getSubfields(code).reduce<string[]>(
      (values, subfield) => (typeof subfield.value === "string" ? [...values, subfield.value] : values),
      []
    );
  }

  /**
   * Return the first subfield with a specific code. Returns undefined if none found.
   */
  getSubfield(code: string) {
    return this.getSubfields(code)?.[0];
  }
}

export class MarcRecord {
  leader: string;
  controlFields: ControlField[];
  dataFields: DataField[];

  constructor({ leader, controlFields, dataFields }: any) {
    this.leader = leader;
    this.controlFields = controlFields;
    this.dataFields = dataFields;
  }

  /**
   * Return all ControlField that matches the specified tag. Returns empty array if no match is found.
   */
  getControlFields(tag: string | RegExp) {
    return this.controlFields.filter((field) => match(tag, field.tag));
  }

  /**
   * Return the first ControlField that matches the specified tag. Returns undefined if no match is found.
   */
  getControlField(tag: string | RegExp) {
    return this.getControlFields(tag)?.[0];
  }

  /**
   * Return part of ControlField by position. End parameter is inclusive (MARC convention)
   */
  getControlFieldPart(tag: string, start: number, end: number) {
    const field = this.getControlField(tag);
    if (field?.value) {
      return field.value.substring(start, end + 1);
    }
  }

  /**
   * Return all DataField that matches the specified tag. Returns empty array if no match is found.
   */
  getDataFields(tag: string | RegExp): DataField[] {
    return this.dataFields.filter((field) => match(tag, field.tag));
  }

  /**
   * Get localized fields ($9) if found, fallback to all fields otherwise.
   */
  getLocalizedFields(tag: string | RegExp, language: string) {
    const fields = this.getDataFields(tag);
    const localizedFields = fields.filter((field) => field.getSubfield("9")?.value === language);
    return localizedFields.length ? localizedFields : fields;
  }

  /**
   * Return the first DataField that matches the specified tag. Returns undefined if no match is found.
   */
  getDataField(tag: string | RegExp): DataField | undefined {
    return this.getDataFields(tag)?.[0];
  }

  /**
   * Return record ID from 001
   */
  getRecordId() {
    return this.getControlField("001")?.value;
  }

  /**
   * Return ISBN from first 020$a
   */
  getIsbn() {
    return normalizeIsbn(this.getDataField("020")?.getSubfield("a")?.value);
  }

  /**
   * Return media type from various fields
   *
   * Source: https://github.com/biblioteksentralen/aja-catmandu/blob/main/app%2Fmarc2json.fix
   */
  getWorkType(): WorkType | undefined {
    if (this.getControlField("007")?.value?.[0] === "c" && (this.leader?.[6] === "a" || this.leader?.[6] === undefined))
      return "Language based work"; //ebook is mapped to book because we want the equivalent of workType
    // Graphic novels, picture books, etc
    if (this.leader?.[6] === "k" && this.getControlField("007")?.value?.slice(0, 2) === "ta")
      return "Language based work";
    if (this.getControlField("007")?.value?.[0] === "v") return "Moving image work";
    if (this.leader?.[6] === "a") return "Language based work";
    if (this.leader?.[6] === "i") return "Language based work"; //audiobook is mapped to book because we want the equivalent of workType
    if (this.leader?.[6] === "m" && this.getControlField("008")?.value?.[26] === "g") return "Interactive work";
    return undefined;
  }

  /**
   * Return title from 245
   */
  getTitle() {
    const title = this.joinSubfields("245", ["a", "b"]);
    const part = this.joinSubfields("245", ["n", "p"], ". ");
    return part ? `${title}. ${part}` : title;
  }

  /**
   * Return combination of subfields glued together as single string.
   */
  joinSubfields(fieldTag: string, subfieldCodes: string[], glue = " : "): string | undefined {
    const field = this.getDataField(fieldTag);
    return (
      subfieldCodes
        .map((code) => field?.getSubfield(code)?.value)
        .filter((value: string | undefined) => typeof value === "string")
        .join(glue) || undefined
    );
  }

  /**
   * Return edition from 250
   */
  getEdition() {
    return this.joinSubfields("250", ["a", "b"]);
  }

  /**
   * Return publication year from 264.
   *
   * A more consistent value could be retrieved from 008/07-10, but not available in NORMARC
   */
  getPublicationYear() {
    return trimBrackets(
      this.getDataField("264")?.getSubfield("c")?.value || this.getDataField("260")?.getSubfield("c")?.value
    );
  }

  /**
   * Return author from 100/700/110/osv.?
   */
  getAuthor() {
    for (const tag of ["100", "700", "110", "710", "111", "711"]) {
      const field = this.getDataField(tag);
      if (field?.getSubfield("a")) {
        return field?.getSubfield("a")?.value;
      }
    }
  }

  /**
   * Return language
   */
  getLanguage() {
    return this.getControlFieldPart("008", 35, 37);
  }

  /**
   * Return summary
   */
  getSummary() {
    return this.getDataField("520")?.getSubfield("a")?.value;
  }

  /**
   * Return subjects, filtered by language ($9). If no language-tagged subject fields are found in the record,
   * which is typical for older records, all subjects are returned instead.
   */
  getSubjects(language: string) {
    return this.getLocalizedFields(/650|651/, language).map((field) => field.getSubfieldValues(/[a-z]/).join(" – "));
  }

  /**
   * Return subjects, filtered by language ($9). If no language-tagged subject fields are found in the record,
   * which is typical for older records, all subjects are returned instead.
   */
  getGenres(language: string) {
    return this.getLocalizedFields(/655/, language).map((field) => field.getSubfieldValues(/[a-z]/).join(" – "));
  }
}

/**
 * Parse xml2js type MARC record
 * Contains MIT licensed code from https://github.com/NatLibFi/marc-record-serializers/blob/master/src/marcxml.js
 */
function parseSubfields(subfields: RawSubfield[]): Subfield[] {
  return subfields.map(({ _: value, $: { code } }) => {
    return {
      code: String(code),
      value: value === undefined ? undefined : String(value),
    };
  });
}

export function marcRecordFromXml2JsRecord(rawRecord: RawMarcRecord): MarcRecord {
  const leader = String(rawRecord.leader?.[0] || "");
  const controlFields: ControlField[] = (rawRecord.controlfield || []).map(({ _: value, $: { tag } }) => ({
    tag,
    value,
  }));
  const dataFields: DataField[] = (rawRecord.datafield || []).map(({ subfield, $: { tag, ind1, ind2 } }) => {
    return new DataField({ tag, ind1, ind2, subfields: parseSubfields(subfield || []) });
  });
  return new MarcRecord({ leader, controlFields, dataFields });
}
